import { apiClient, handleApiSuccess } from './api';

export interface Discussion {
  id: string;
  clubId: string;
  authorId: string;
  authorName: string;
  authorRole: string;
  authorProfilePicture?: string;
  title: string;
  content: string;
  category: 'general' | 'technical' | 'events' | 'support';
  replies: number;
  lastReply: string;
  timestamp: string;
  isPinned: boolean;
}

export interface DiscussionThread {
  id: string;
  clubId: string;
  authorId: string;
  authorName: string;
  authorRole: string;
  authorProfilePicture?: string;
  title: string;
  content: string;
  category: 'general' | 'technical' | 'events' | 'support';
  timestamp: string;
  isPinned: boolean;
  replies: Array<{
    id: string;
    authorId: string;
    authorName: string;
    authorRole: string;
    authorProfilePicture?: string;
    content: string;
    timestamp: string;
    upvotes?: number;
    downvotes?: number;
    netVotes?: number;
    userVote?: 'upvote' | 'downvote' | null;
  }>;
}

export interface CreateDiscussionRequest {
  title: string;
  content: string;
  category: 'general' | 'technical' | 'events' | 'support';
}

export interface CreateReplyRequest {
  content: string;
}

export interface GetDiscussionsResponse {
  discussions: Discussion[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    hasMore: boolean;
  };
}

/**
 * Discussion Service
 * Handles all club discussions related API calls
 */
export class DiscussionService {
  /**
   * Get club discussions with pagination
   */
  static async getClubDiscussions(
    clubId: string,
    page: number = 1,
    limit: number = 20,
    category?: string
  ): Promise<GetDiscussionsResponse> {
    try {
      let url = `/discussions/club/${clubId}?page=${page}&limit=${limit}`;
      if (category) {
        url += `&category=${category}`;
      }

      const response = await apiClient.get(url);

      console.log('🔍 NEW DEBUG - Raw apiClient response:', response); // Debug log

      // apiClient.get() returns ApiResponse<T> directly, not wrapped in axios response
      const backendResponse = response;

      // Detailed debugging
      console.log('🔍 Response keys:', Object.keys(backendResponse || {}));
      console.log('🔍 Has success field:', 'success' in (backendResponse || {}));
      console.log('🔍 Has data field:', 'data' in (backendResponse || {}));
      console.log('🔍 Has discussions field:', 'discussions' in (backendResponse || {}));
      console.log('🔍 Success value:', backendResponse?.success);
      console.log('🔍 Success type:', typeof backendResponse?.success);

      // Handle two possible response formats:
      // 1. Wrapped format: { success: true, data: { discussions: [...], pagination: {...} } }
      // 2. Direct format: { discussions: [...], pagination: {...} }

      if (backendResponse && backendResponse.success && backendResponse.data) {
        // Format 1: Wrapped response
        console.log('✅ Using wrapped response format');
        return backendResponse.data;
      } else if (backendResponse && (backendResponse as any).discussions && (backendResponse as any).pagination) {
        // Format 2: Direct response
        console.log('✅ Using direct response format');
        return backendResponse as unknown as GetDiscussionsResponse;
      } else {
        console.warn('❌ Unexpected response format:', backendResponse);
        return {
          discussions: [],
          pagination: {
            currentPage: page,
            totalPages: 1,
            totalItems: 0,
            hasMore: false
          }
        };
      }
    } catch (error) {
      console.error('Error fetching club discussions:', error);
      // Return empty result on error
      return {
        discussions: [],
        pagination: {
          currentPage: page,
          totalPages: 1,
          totalItems: 0,
          hasMore: false
        }
      };
    }
  }

  /**
   * Get a specific discussion thread with replies
   */
  static async getDiscussionThread(discussionId: string): Promise<DiscussionThread> {
    try {
      const response = await apiClient.get(`/discussions/${discussionId}`);

      console.log('Get discussion thread API response:', response); // Debug log

      // apiClient.get() returns ApiResponse<T> directly
      const backendResponse = response;
      if (backendResponse && backendResponse.success === true && backendResponse.data) {
        return backendResponse.data;
      } else {
        throw new Error('Invalid response format from server');
      }
    } catch (error) {
      console.error('Error fetching discussion thread:', error);
      throw error;
    }
  }

  /**
   * Create a new discussion
   */
  static async createDiscussion(clubId: string, data: CreateDiscussionRequest): Promise<Discussion> {
    try {
      const response = await apiClient.post(`/discussions/club/${clubId}`, data);

      console.log('Create discussion API response:', response); // Debug log

      // apiClient.post() returns ApiResponse<T> directly
      const backendResponse = response;
      if (backendResponse && backendResponse.success === true && backendResponse.data) {
        return backendResponse.data;
      } else {
        throw new Error('Invalid response format from server');
      }
    } catch (error) {
      console.error('Error creating discussion:', error);
      throw error;
    }
  }

  /**
   * Reply to a discussion
   */
  static async replyToDiscussion(discussionId: string, data: CreateReplyRequest): Promise<void> {
    try {
      const response = await apiClient.post(`/discussions/${discussionId}/replies`, data);

      console.log('Reply to discussion API response:', response); // Debug log

      // apiClient.post() returns ApiResponse<T> directly
      const backendResponse = response;
      if (backendResponse.success !== true) {
        throw new Error(backendResponse.message || 'Failed to add reply');
      }
    } catch (error) {
      console.error('Error adding reply:', error);
      throw error;
    }
  }

  /**
   * Upvote a reply
   */
  static async upvoteReply(discussionId: string, replyId: string): Promise<{
    upvotes: number;
    downvotes: number;
    netVotes: number;
    userVote: 'upvote' | 'downvote' | null
  }> {
    try {
      const response = await apiClient.post(`/discussions/${discussionId}/replies/${replyId}/upvote`);

      console.log('Upvote reply API response:', response);

      const backendResponse = response;
      if (backendResponse && backendResponse.success === true && backendResponse.data) {
        return backendResponse.data;
      } else {
        throw new Error('Invalid response format from server');
      }
    } catch (error) {
      console.error('Error upvoting reply:', error);
      throw error;
    }
  }

  /**
   * Downvote a reply
   */
  static async downvoteReply(discussionId: string, replyId: string): Promise<{
    upvotes: number;
    downvotes: number;
    netVotes: number;
    userVote: 'upvote' | 'downvote' | null
  }> {
    try {
      const response = await apiClient.post(`/discussions/${discussionId}/replies/${replyId}/downvote`);

      console.log('Downvote reply API response:', response);

      const backendResponse = response;
      if (backendResponse && backendResponse.success === true && backendResponse.data) {
        return backendResponse.data;
      } else {
        throw new Error('Invalid response format from server');
      }
    } catch (error) {
      console.error('Error downvoting reply:', error);
      throw error;
    }
  }

  /**
   * Delete a discussion (Author or club admin only)
   * TODO: Implement delete endpoints in backend
   */
  static async deleteDiscussion(discussionId: string): Promise<void> {
    // TODO: Backend delete endpoint not implemented yet
    throw new Error('Delete discussion functionality not yet implemented');
  }

  /**
   * Delete a reply (Author or club admin only)
   * TODO: Implement delete endpoints in backend
   */
  static async deleteReply(discussionId: string, replyId: string): Promise<void> {
    // TODO: Backend delete endpoint not implemented yet
    throw new Error('Delete reply functionality not yet implemented');
  }

  /**
   * Pin/unpin a discussion (Club admin only)
   */
  static async togglePin(discussionId: string): Promise<{ isPinned: boolean }> {
    const response = await apiClient.post(`/discussions/${discussionId}/pin`);
    return handleApiSuccess(response);
  }

  /**
   * Get available discussion categories
   */
  static getCategories(): Array<{ value: string; label: string; icon: string }> {
    return [
      { value: 'general', label: 'General', icon: '💬' },
      { value: 'technical', label: 'Technical', icon: '🔧' },
      { value: 'events', label: 'Events', icon: '📅' },
      { value: 'support', label: 'Support', icon: '🆘' },
    ];
  }
}

export default DiscussionService;